import React, { useState, useEffect, useCallback, useMemo } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import moment from 'moment-timezone';

const BotCodeManager = () => {
    // --- Tabs State ---
    const [activeTab, setActiveTab] = useState('codes'); // 'codes' or 'reminders'

    // --- Data States ---
    const [codes, setCodes] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    // --- Modal States ---
    const [showFormModal, setShowFormModal] = useState(false);
    const [showReminderModal, setShowReminderModal] = useState(false);

    // --- Form States (নতুন isResultBot ও resultButtons যুক্ত করা হয়েছে) ---
    const initialFormState = { 
        id: null, 
        websiteName: '', 
        securityCode: '', 
        isSubCode: false, 
        mainSecurityCode: '', 
        replyApiUrl: '',
        isResultBot: false,
        resultButtons: []
    };
    const [form, setForm] = useState(initialFormState);

    const initialReminderState = { id: null, targetCode: '', message: '', scheduleType: 'once', date: moment().format('YYYY-MM-DD'), time: moment().format('HH:mm') };
    const [reminderForm, setReminderForm] = useState(initialReminderState);

    // =========================================================
    // --- Data Fetcher ---
    // =========================================================
    const fetchData = useCallback(() => {
        setLoading(true);
        setError(null);
        socketService.emit('getBotCodes');
        socketService.emit('getReminders'); 
    }, []);
    
    const handleSuccess = (data) => {
        setLoading(false);
        if (data && data.message) {
            setSuccessMessage(data.message);
            setError(null);
            setTimeout(() => setSuccessMessage(null), 3000); 
        }
    };

    const handleError = (data) => {
        setLoading(false);
        if (data && data.message) {
            setError(data.message);
            setSuccessMessage(null);
        }
    }

    // =========================================================
    // --- Socket Listeners ---
    // =========================================================
    useEffect(() => {
        const socket = socketService.getSocket();
        if (!socket || !socket.connected) socketService.connect();

        const handleConnect = () => fetchData(); 
        const handleCodesData = (data) => { setLoading(false); setCodes(data || []); };
        const handleRemindersData = (data) => { setLoading(false); setReminders(data || []); };

        socketService.on('connect', handleConnect); 
        socketService.on('botCodesData', handleCodesData);
        socketService.on('remindersData', handleRemindersData); 
        socketService.on('action_success', handleSuccess);
        socketService.on('error', handleError);
        
        if (socket && socket.connected) fetchData();

        return () => {
            socketService.off('connect', handleConnect); 
            socketService.off('botCodesData', handleCodesData);
            socketService.off('remindersData', handleRemindersData);
            socketService.off('action_success', handleSuccess);
            socketService.off('error', handleError);
        };
    }, [fetchData]);

    // =========================================================
    // --- Grouping Logic (Bot Codes) ---
    // =========================================================
    const groupedCodes = useMemo(() => {
        const groupedData = [];
        let indexCounter = 1;
        const mainCodesList = codes.filter(c => !c.mainSecurityCode);
        const subCodesList = codes.filter(c => c.mainSecurityCode);

        mainCodesList.forEach(main => {
            groupedData.push({ ...main, isGroupMain: true, displayIndex: indexCounter++ });
            const children = subCodesList.filter(sub => sub.mainSecurityCode === main.securityCode);
            children.forEach(child => { groupedData.push({ ...child, isGroupSub: true, displayIndex: '' }); });
        });

        subCodesList.forEach(sub => {
            if (!mainCodesList.some(m => m.securityCode === sub.mainSecurityCode)) {
                groupedData.push({ ...sub, isOrphan: true, displayIndex: indexCounter++ });
            }
        });
        return groupedData;
    }, [codes]);

    // =========================================================
    // --- Handlers (Bot Codes) ---
    // =========================================================
    const closeAllModals = () => {
        setShowFormModal(false);
        setShowReminderModal(false);
        setForm(initialFormState);
        setReminderForm(initialReminderState);
    };

    const handleSaveCode = (e) => {
        e.preventDefault();
        const payload = { 
            websiteName: form.websiteName, 
            securityCode: form.securityCode, 
            mainSecurityCode: form.isSubCode ? form.mainSecurityCode : null,
            replyApiUrl: form.replyApiUrl,
            isResultBot: form.isResultBot,
            resultButtons: form.isResultBot ? form.resultButtons : [] 
        };
        setLoading(true);
        form.id ? socketService.emit('updateBotCode', { ...payload, _id: form.id }) : socketService.emit('createBotCode', payload);
        closeAllModals();
    };

    // এডিট বাটন হ্যান্ডলার
    const handleEditCode = (codeData) => {
        setForm({
            id: codeData._id,
            websiteName: codeData.websiteName,
            securityCode: codeData.securityCode,
            isSubCode: !!codeData.mainSecurityCode,
            mainSecurityCode: codeData.mainSecurityCode || '',
            replyApiUrl: codeData.replyApiUrl || '',
            isResultBot: codeData.isResultBot || false,
            resultButtons: codeData.resultButtons || []
        });
        setShowFormModal(true);
    };

    const handleDeleteCode = (id) => {
        if (!window.confirm('Are you sure you want to delete this Bot Code?')) return;
        setLoading(true);
        socketService.emit('deleteBotCode', { _id: id });
    };

    const handleCopyLink = (securityCode) => {
        const botUsername = "Dabitpay_support_bot"; 
        navigator.clipboard.writeText(`https://t.me/${botUsername}?start=${securityCode}`).then(() => {
            setSuccessMessage(`✅ Link copied!`);
            setTimeout(() => setSuccessMessage(null), 3000);
        });
    };

    // --- Result Bot Dynamic Button Handlers ---
    const handleAddResultButton = () => {
        setForm({
            ...form,
            resultButtons: [...form.resultButtons, { buttonName: '', apiUrl: '', replyText: '' }]
        });
    };

    const handleRemoveResultButton = (index) => {
        const updatedButtons = [...form.resultButtons];
        updatedButtons.splice(index, 1);
        setForm({ ...form, resultButtons: updatedButtons });
    };

    const handleResultButtonChange = (index, field, value) => {
        const updatedButtons = [...form.resultButtons];
        updatedButtons[index][field] = value;
        setForm({ ...form, resultButtons: updatedButtons });
    };

    // =========================================================
    // --- Handlers (Reminders) ---
    // =========================================================
    const handleReminderChange = (e) => {
        const { name, value } = e.target;
        setReminderForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveReminder = (e) => {
        e.preventDefault();
        if(!reminderForm.targetCode) return alert("Select a Target Admin!");
        if(!reminderForm.message) return alert("Message cannot be empty!");

        const nextRunAt = new Date(`${reminderForm.date}T${reminderForm.time}:00`);
        const payload = {
            targetCode: reminderForm.targetCode,
            message: reminderForm.message,
            scheduleType: reminderForm.scheduleType,
            nextRunAt: nextRunAt,
            timeString: reminderForm.time
        };

        setLoading(true);
        socketService.emit('createReminder', payload);
        closeAllModals();
    };

    const handleDeleteReminder = (id) => {
        if (!window.confirm('Delete this reminder?')) return;
        setLoading(true);
        socketService.emit('deleteReminder', { _id: id });
    };

    const handleToggleReminderStatus = (id) => {
        setLoading(true);
        socketService.emit('toggleReminderStatus', { _id: id });
    };

    // =========================================================
    // --- Render ---
    // =========================================================
    return (
        <Layout>
            <div className="main-content-area py-3 px-4">
                
                {/* Header & Tabs */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="section-title mb-0">🤖 Telegram Bot Settings</h4>
                    <div className="tab-buttons bg-white shadow-sm rounded p-1">
                        <button className={`tab-btn ${activeTab === 'codes' ? 'active' : ''}`} onClick={() => setActiveTab('codes')}>🛡️ Bot Codes</button>
                        <button className={`tab-btn ${activeTab === 'reminders' ? 'active' : ''}`} onClick={() => setActiveTab('reminders')}>⏰ Reminders</button>
                    </div>
                </div>

                {successMessage && <div className="alert alert-success py-2 px-3 mb-3 small shadow-sm border-0 bg-seen">{successMessage}</div>}
                {error && <div className="alert alert-danger py-2 px-3 mb-3 small shadow-sm border-0 bg-fake">{error}</div>}
                
                {/* Action Bar */}
                <div className="filter-card mb-4 shadow-sm d-flex justify-content-between align-items-center">
                    <div>
                        <span className="field-label d-block">QUICK ACTIONS</span>
                        <small className="text-muted">
                            {activeTab === 'codes' ? 'Manage your Telegram verification codes' : 'Manage automated scheduled notifications'}
                        </small>
                    </div>
                    {activeTab === 'codes' ? (
                        <button onClick={() => setShowFormModal(true)} className="btn-apply py-2 px-4">➕ Add New Code</button>
                    ) : (
                        <button onClick={() => setShowReminderModal(true)} className="btn-apply py-2 px-4 bg-warning text-dark border-0">➕ Add Reminder</button>
                    )}
                </div>

                {/* --- TAB CONTENT: BOT CODES --- */}
                {activeTab === 'codes' && (
                    <div className="table-container shadow-sm" style={{ overflowX: 'auto' }}>
                        {loading && codes.length === 0 ? <div className="text-center p-5 text-muted">Loading data...</div> : (
                            <table className="custom-tree-table">
                                <thead>
                                    <tr>
                                        <th className="text-center">#</th>
                                        <th>Security Code & Name</th>
                                        <th>Reply API</th>
                                        <th>Chat ID</th>
                                        <th>Status</th>
                                        <th className="text-end" style={{minWidth: '150px'}}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedCodes.length > 0 ? groupedCodes.map((c) => (
                                        <tr key={c._id} className={c.isGroupSub ? 'sub-code-row' : 'main-code-row'}>
                                            <td className="text-center fw-bold text-muted">{c.displayIndex}</td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    {c.isGroupSub && <span className="sub-tree-icon">↳</span>}
                                                    <div>
                                                        <span className="user-primary" style={{fontSize: '13px'}}>
                                                            {c.websiteName} 
                                                            {c.isResultBot && <span className="badge bg-warning text-dark ms-2" style={{fontSize: '9px', verticalAlign: 'middle'}}>⚡ ResultBot</span>}
                                                        </span><br/>
                                                        <div className="device-secondary px-2 py-1" style={{fontSize: '13px', color: c.isGroupSub ? '#c2410c' : '#0369a1', marginTop: '4px'}}>{c.securityCode}</div>
                                                        <div style={{fontSize: '10px', color: c.isGroupSub ? '#c2410c' : '#16a34a', marginTop: '4px', fontWeight: 'bold'}}>
                                                            {c.isGroupSub ? `Sub-code of: ${c.mainSecurityCode}` : 'Main Code'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {c.replyApiUrl ? (
                                                    <div style={{fontSize: '11px', color: '#059669', background: '#d1fae5', padding: '4px 8px', borderRadius: '4px', maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} title={c.replyApiUrl}>
                                                        🔗 {c.replyApiUrl}
                                                    </div>
                                                ) : (
                                                    <span style={{fontSize: '11px', color: '#94a3b8'}}>Not Set</span>
                                                )}
                                            </td>
                                            <td>{c.telegramChatId ? <span style={{color: '#16a34a', fontWeight: '700', fontSize: '12px'}}>{c.telegramChatId}</span> : <span style={{color: '#dc2626', fontSize: '11px'}}>Not Connected</span>}</td>
                                            <td>
                                                <div className="form-check form-switch m-0 p-0 d-flex align-items-center">
                                                    <input className="form-check-input m-0" type="checkbox" role="switch" checked={c.isActive} onChange={() => { setLoading(true); socketService.emit('toggleBotCodeStatus', { _id: c._id }); }} style={{ cursor: 'pointer' }} />
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                <button className="btn btn-sm btn-light border text-primary me-1 mb-1" style={{fontSize: '11px', fontWeight: '600'}} onClick={() => handleCopyLink(c.securityCode)}>📋 Link</button>
                                                <button className="btn btn-sm btn-light border text-success me-1 mb-1" style={{fontSize: '11px', fontWeight: '600'}} onClick={() => handleEditCode(c)}>✏️ Edit</button>
                                                <button className="btn btn-sm btn-light border text-danger mb-1" style={{fontSize: '11px', fontWeight: '600'}} onClick={() => handleDeleteCode(c._id)}>🗑 Del</button>
                                            </td>
                                        </tr>
                                    )) : <tr><td colSpan="6" className="text-center py-4 text-muted">No Bot codes found.</td></tr>}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* --- TAB CONTENT: REMINDERS --- */}
                {activeTab === 'reminders' && (
                    <div className="table-container shadow-sm">
                        {loading && reminders.length === 0 ? <div className="text-center p-5 text-muted">Loading data...</div> : (
                            <table className="custom-tree-table">
                                <thead style={{background: '#fef3c7'}}>
                                    <tr>
                                        <th>Target</th>
                                        <th>Message</th>
                                        <th>Type</th>
                                        <th>Next Schedule</th>
                                        <th>Status</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reminders.length > 0 ? reminders.map((r) => {
                                        const targetObj = codes.find(c => c.securityCode === r.targetCode);
                                        const targetName = targetObj ? targetObj.websiteName : r.targetCode;
                                        return (
                                        <tr key={r._id}>
                                            <td>
                                                <span className="user-primary d-block" style={{fontSize: '13px'}}>{targetName}</span>
                                                <small className="text-muted">{r.targetCode}</small>
                                            </td>
                                            <td>
                                                <div style={{fontSize: '12px', maxWidth: '250px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', background: '#f1f5f9', padding: '5px 8px', borderRadius: '4px'}}>
                                                    {r.message}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={`badge ${r.scheduleType === 'daily' ? 'bg-primary' : r.scheduleType === 'monthly' ? 'bg-success' : 'bg-secondary'}`} style={{textTransform: 'capitalize'}}>{r.scheduleType}</span>
                                            </td>
                                            <td>
                                                <div className="date-bold text-danger">{moment(r.nextRunAt).format('YYYY-MM-DD')}</div>
                                                <div className="time-muted fw-bold">{moment(r.nextRunAt).format('hh:mm A')}</div>
                                            </td>
                                            <td>
                                                <div className="form-check form-switch m-0 p-0 d-flex align-items-center">
                                                    <input className="form-check-input m-0" type="checkbox" role="switch" checked={r.isActive} onChange={() => handleToggleReminderStatus(r._id)} style={{ cursor: 'pointer' }} />
                                                    <label className="ms-2" style={{fontSize: '11px', color: r.isActive ? 'green' : 'gray'}}>{r.isActive ? 'Active' : 'Finished/Off'}</label>
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                <button className="btn btn-sm btn-light border text-danger" style={{fontSize: '11px', fontWeight: '600'}} onClick={() => handleDeleteReminder(r._id)}>🗑 Delete</button>
                                            </td>
                                        </tr>
                                    )}) : <tr><td colSpan="6" className="text-center py-4 text-muted">No reminders found.</td></tr>}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>

            {/* --- MODALS --- */}
            {/* Modal: Bot Code */}
            {showFormModal && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal-dialog">
                        <div className="custom-modal-content">
                            <div className="modal-header bg-primary border-0" style={{ color: 'white' }}>
                                <h6 className="m-0 fw-bold">{form.id ? '✏️ Edit Bot Code' : '🤖 Add Bot Code'}</h6>
                                <button type="button" className="btn-close" style={{ filter: 'invert(1)' }} onClick={closeAllModals}></button>
                            </div>
                            <form onSubmit={handleSaveCode}>
                                <div className="modal-body p-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                    <div className="mb-3">
                                        <label className="field-label">Website Name</label>
                                        <input 
                                            type="text" 
                                            value={form.websiteName} 
                                            onChange={(e) => setForm({...form, websiteName: e.target.value})} 
                                            className="modern-input" 
                                            placeholder="e.g. Amer Security" 
                                            required 
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="field-label">Security Code</label>
                                        <input 
                                            type="text" 
                                            value={form.securityCode} 
                                            onChange={(e) => setForm({...form, securityCode: e.target.value})} 
                                            className="modern-input" 
                                            placeholder="Enter unique code" 
                                            required 
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="field-label">Reply API URL (Optional)</label>
                                        <input 
                                            type="url" 
                                            value={form.replyApiUrl} 
                                            onChange={(e) => setForm({...form, replyApiUrl: e.target.value})} 
                                            className="modern-input" 
                                            placeholder="https://api.yourwebsite.com/reply" 
                                        />
                                        <small className="text-muted mt-1 d-block" style={{fontSize: '10px'}}>Where admin replies will be sent automatically.</small>
                                    </div>

                                    <div className="mb-3 form-check d-flex align-items-center">
                                        <input 
                                            type="checkbox" 
                                            className="form-check-input mt-0 me-2" 
                                            id="isSubCode" 
                                            checked={form.isSubCode} 
                                            onChange={(e) => setForm({...form, isSubCode: e.target.checked})} 
                                            style={{ cursor: 'pointer' }}
                                        />
                                        <label className="form-check-label field-label mb-0" htmlFor="isSubCode" style={{ cursor: 'pointer' }}>
                                            Is this a Sub-Code?
                                        </label>
                                    </div>
                                    
                                    {form.isSubCode && (
                                        <div className="mb-3 p-3 rounded" style={{ background: '#f8fafc', border: '1px dashed #cbd5e1' }}>
                                            <label className="field-label">Select Main Security Code</label>
                                            <select 
                                                value={form.mainSecurityCode} 
                                                onChange={(e) => setForm({...form, mainSecurityCode: e.target.value})} 
                                                className="modern-input" 
                                                required={form.isSubCode}
                                            >
                                                <option value="">-- Select Main Code --</option>
                                                {codes.filter(c => !c.mainSecurityCode).map(c => (
                                                    <option key={c._id} value={c.securityCode}>
                                                        {c.websiteName} ({c.securityCode})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <hr className="my-4 text-muted" />

                                    {/* --- Result Bot Section --- */}
                                    <div className="mb-3 form-check form-switch d-flex align-items-center">
                                        <input 
                                            type="checkbox" 
                                            className="form-check-input mt-0 me-2" 
                                            id="isResultBot" 
                                            checked={form.isResultBot} 
                                            onChange={(e) => setForm({...form, isResultBot: e.target.checked})} 
                                            style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                        />
                                        <label className="form-check-label field-label mb-0" htmlFor="isResultBot" style={{ cursor: 'pointer', fontSize: '11px', color: '#0369a1' }}>
                                            ⚡ Enable Result Bot Mode
                                        </label>
                                    </div>

                                    {form.isResultBot && (
                                        <div className="p-3 mb-2 rounded" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <label className="field-label m-0 text-success">Dynamic Result Buttons</label>
                                                <button type="button" className="btn btn-sm btn-success py-1 px-2" onClick={handleAddResultButton} style={{fontSize: '10px', fontWeight: 'bold'}}>➕ Add Button</button>
                                            </div>

                                            {form.resultButtons.length === 0 ? (
                                                <div className="text-muted text-center py-3" style={{fontSize: '11px', background: '#fff', borderRadius: '5px'}}>No buttons added. Click "Add Button" to create one.</div>
                                            ) : (
                                                form.resultButtons.map((btn, index) => (
                                                    <div key={index} className="p-3 mb-3 rounded bg-white shadow-sm" style={{ border: '1px solid #e2e8f0', position: 'relative' }}>
                                                        <div className="d-flex justify-content-between mb-2">
                                                            <span style={{fontSize: '11px', fontWeight: 'bold', color: '#64748b'}}>Button #{index + 1}</span>
                                                            <button type="button" className="text-danger border-0 bg-transparent" onClick={() => handleRemoveResultButton(index)} style={{fontSize: '11px', fontWeight: 'bold', cursor: 'pointer'}}>✖ Remove</button>
                                                        </div>
                                                        <div className="mb-2">
                                                            <input type="text" className="modern-input" placeholder="Button Name (e.g. Reject)" value={btn.buttonName} onChange={(e) => handleResultButtonChange(index, 'buttonName', e.target.value)} required />
                                                        </div>
                                                        <div className="mb-2">
                                                            <input type="url" className="modern-input" placeholder="Action API URL" value={btn.apiUrl} onChange={(e) => handleResultButtonChange(index, 'apiUrl', e.target.value)} required />
                                                        </div>
                                                        <div>
                                                            <input type="text" className="modern-input" placeholder="Reply Message (e.g. Action Successful!)" value={btn.replyText} onChange={(e) => handleResultButtonChange(index, 'replyText', e.target.value)} required />
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer border-0 bg-light">
                                    <button type="button" className="btn btn-reset flex-fill py-2" onClick={closeAllModals}>Cancel</button>
                                    <button type="submit" className="btn btn-apply flex-fill py-2 bg-primary text-white border-0" disabled={loading}>
                                        {loading ? 'Saving...' : (form.id ? 'Update Code' : 'Save Code')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Reminder */}
            {showReminderModal && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal-dialog">
                        <div className="custom-modal-content">
                            <div className="modal-header bg-warning text-dark border-0">
                                <h6 className="m-0 fw-bold">⏰ Add New Reminder</h6>
                                <button type="button" className="btn-close" onClick={closeAllModals}></button>
                            </div>
                            <form onSubmit={handleSaveReminder}>
                                <div className="modal-body p-4">
                                    <div className="mb-3">
                                        <label className="field-label">Target Admin/Group</label>
                                        <select name="targetCode" value={reminderForm.targetCode} onChange={handleReminderChange} className="modern-input" required>
                                            <option value="">-- Select Target --</option>
                                            {codes.map(c => (
                                                <option key={c._id} value={c.securityCode}>{c.websiteName} ({c.securityCode}) {c.mainSecurityCode ? '- Sub' : '- Main'}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="field-label">Schedule Type</label>
                                            <select name="scheduleType" value={reminderForm.scheduleType} onChange={handleReminderChange} className="modern-input">
                                                <option value="once">Once (One time)</option>
                                                <option value="daily">Daily</option>
                                                <option value="monthly">Monthly</option>
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="field-label">Date (Start Date)</label>
                                            <input type="date" name="date" value={reminderForm.date} onChange={handleReminderChange} className="modern-input" required />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="field-label">Time</label>
                                        <input type="time" name="time" value={reminderForm.time} onChange={handleReminderChange} className="modern-input" required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="field-label">Notification Message</label>
                                        <textarea name="message" value={reminderForm.message} onChange={handleReminderChange} className="modern-input" rows="3" placeholder="Enter your notification text here..." required></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 bg-light">
                                    <button type="button" className="btn btn-reset flex-fill py-2" onClick={closeAllModals}>Cancel</button>
                                    <button type="submit" className="btn btn-apply flex-fill py-2 bg-dark text-white border-0" disabled={loading}>{loading ? 'Saving...' : 'Set Reminder'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .section-title { color: #334155; font-weight: 700; }
                .count-badge { background: #fff; padding: 5px 15px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; }
                .filter-card { background: #ffffff; padding: 15px; border-radius: 12px; }
                .field-label { font-size: 10px; font-weight: 700; color: #64748b; margin-bottom: 5px; text-transform: uppercase; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 8px 12px; border-radius: 8px; font-size: 13px; width: 100%; outline: none; }
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 13px; border: none; }
                .btn-reset { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; }
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; }
                .custom-tree-table { width: 100%; border-collapse: collapse; }
                .custom-tree-table thead { background: #e2e8f0; color: #475569; }
                .custom-tree-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; border-bottom: 2px solid #cbd5e1; text-align: left; }
                .custom-tree-table td { padding: 12px 15px; vertical-align: middle; }
                .main-code-row { border-top: 2px solid #e2e8f0; border-bottom: 1px solid #edf2f7; }
                .sub-code-row { background-color: #f8fafc; border-bottom: 1px dashed #e2e8f0; }
                .sub-tree-icon { color: #94a3b8; font-size: 20px; font-weight: 300; margin-right: 12px; margin-left: 10px; display: inline-block; transform: translateY(-4px); }
                .user-primary { color: #0369a1; font-weight: 700; font-size: 14px; }
                .device-secondary { font-size: 10px; color: #0000009d; font-weight: 800; background: #e2e8f0; display: inline-block; padding: 1px 6px; border-radius: 4px; margin-top: 3px; }
                .date-bold { color: #334155; font-weight: 700; font-size: 13px; }
                .bg-seen { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
                .bg-fake { background: #fee2e2; color: #dc2626; border-color: #fecaca; }
                
                /* TAB Styles */
                .tab-buttons { display: flex; gap: 5px; }
                .tab-btn { background: transparent; border: none; padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; transition: 0.3s; }
                .tab-btn.active { background: #0284c7; color: #fff; }
                
                /* Modal Styles */
                .custom-modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); z-index: 9999; display: flex; justify-content: center; padding-top: 60px; overflow-y: auto;}
                .custom-modal-dialog { background: #fff; width: 90%; max-width: 500px; border-radius: 12px; margin-bottom: 50px; height: fit-content;}
                .modal-header { padding: 15px 20px; border-bottom: 1px solid #eee; display: flex; justify-content: space-between;}
                .modal-body { padding: 20px; }
                .modal-footer { padding: 15px 20px; display: flex; gap: 10px;}
                .btn-close { border: none; background: transparent; font-weight: bold; cursor: pointer; }
            `}</style>
        </Layout>
    );
};

export default BotCodeManager;